import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppContext } from '../App'; 
import authService from '../services/authService';
import config from '../config';
import { trackEvent } from '../analytics';

function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, fetchUserData } = useContext(AppContext);

  const [inviteDetails, setInviteDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);

  const [authMode, setAuthMode] = useState('choose'); // choose | login | register
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  useEffect(() => {
    const fetchInviteDetails = async () => {
      setIsLoading(true);
      setError('');
      try {
        const response = await fetch(`${config.apiBaseUrl}/api/invites/${token}`, {
          headers: {
            // No auth headers needed for fetching public invite details
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // Handle specific errors from backend if available
          if (response.status === 404) {
            throw new Error('Invite not found. The link may be invalid.');
          } else if (response.status === 410) {
            throw new Error(errorData.error || 'This invitation has expired or has already been used.');
          } else {
            throw new Error(errorData.error || `Failed to load invitation details (Status: ${response.status})`);
          }
        }

        const data = await response.json();
        if (data.success && data.invite) {
          setInviteDetails(data.invite); 

          const inviteEmail = (data.invite.email || '').toLowerCase();
          setLoginEmail(inviteEmail);
          setRegisterEmail(inviteEmail);

          const currentEmail = user?.user?.email?.toLowerCase();
          if (user && currentEmail && currentEmail !== inviteEmail) {
            setError(`This invite is for ${data.invite.email}, but you are logged in as ${user.user.email}. Please switch accounts to continue.`);
          } else {
            setError('');
          }
        } else {
          throw new Error(data.error || 'Failed to parse invite details.');
        }
      } catch (err) {
        console.error("Error fetching invite details:", err);
        setError(err.message);
        setInviteDetails(null); 
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchInviteDetails();
    } else {
      setError('No invite token provided.');
      setIsLoading(false);
    }
  }, [token, user]); 

  const handleAcceptInvite = async () => {
    const inviteEmail = inviteDetails?.email?.toLowerCase();
    const currentEmail = user?.user?.email?.toLowerCase();

    if (!inviteDetails || !user || !inviteEmail || !currentEmail || currentEmail !== inviteEmail) {
      setError('Cannot accept invite. Ensure you are logged in with the correct email.');
      return;
    }
    
    setAccepting(true);
    setError('');

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/invites/${token}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders(), 
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to accept invitation (Status: ${response.status})`);
      }

      const data = await response.json();
      if (data.success) {
        console.log('Invite accepted successfully:', data);
        trackEvent('accept_invite', { during: 'logged_in' });
        // Refresh user data (teams) in the context
        await fetchUserData();
        // Navigate to the dashboard
        navigate('/dashboard');
      } else {
        throw new Error(data.error || 'Backend indicated failure but did not provide error message.');
      }
    } catch (err) {
      console.error("Error accepting invite:", err);
      setError(err.message || 'An error occurred while accepting the invitation.');
    } finally {
      setAccepting(false);
    }
  };

  const handleLoginAndAccept = async () => {
    if (!inviteDetails) return;

    setAuthError('');

    const emailToUse = loginEmail?.trim();

    if (!emailToUse) {
      setAuthError('Email is required.');
      return;
    }

    if (!loginPassword) {
      setAuthError('Password is required.');
      return;
    }

    if (inviteDetails.email && inviteDetails.email.toLowerCase() !== emailToUse.toLowerCase()) {
      setAuthError(`This invite is for ${inviteDetails.email}. Update the email to match before continuing.`);
      return;
    }

    setAuthLoading(true);
    try {
      await authService.loginWithInvite(emailToUse, loginPassword, token);
      await fetchUserData();
      navigate('/dashboard');
    } catch (err) {
      console.error('Login + invite redemption failed:', err);
      setAuthError(err instanceof Error ? err.message : 'Failed to log in and accept the invitation.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegisterAndAccept = async () => {
    if (!inviteDetails) return;

    setAuthError('');

    const emailToUse = registerEmail?.trim();

    if (!emailToUse) {
      setAuthError('Email is required.');
      return;
    }

    if (inviteDetails.email && inviteDetails.email.toLowerCase() !== emailToUse.toLowerCase()) {
      setAuthError(`This invite is for ${inviteDetails.email}. Please use the invited email to join.`);
      return;
    }

    if (!registerPassword || !registerConfirmPassword) {
      setAuthError('Please enter and confirm your password.');
      return;
    }

    if (registerPassword !== registerConfirmPassword) {
      setAuthError('Passwords do not match.');
      return;
    }

    if (registerPassword.length < 6) {
      setAuthError('Password must be at least 6 characters long.');
      return;
    }

    setAuthLoading(true);
    try {
      await authService.registerWithInvite(emailToUse, registerPassword, token);
      await fetchUserData();
      navigate('/dashboard');
    } catch (err) {
      console.error('Registration + invite redemption failed:', err);
      setAuthError(err instanceof Error ? err.message : 'Failed to create your account with this invitation.');
    } finally {
      setAuthLoading(false);
    }
  };

  const renderAuthOptions = () => (
    <div className="bg-blue-500/20 text-blue-100 p-3 rounded space-y-3">
      <p className="text-sm">
        To join this team, sign in with the invited email or create a new password for it. We&rsquo;ll handle the invite automatically.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          onClick={() => setAuthMode('login')}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
        >
          I already have an account
        </button>
        <button
          onClick={() => setAuthMode('register')}
          className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded"
        >
          Create an account to join
        </button>
      </div>
    </div>
  );

  const renderLoginForm = () => (
    <div className="bg-white/10 p-4 rounded space-y-3">
      <h3 className="text-lg font-semibold">Sign in &amp; join</h3>
      <p className="text-sm text-gray-200">
        Enter the credentials for <span className="font-semibold">{inviteDetails.email}</span>. We&rsquo;ll redeem the invite as part of the login.
      </p>
      <div className="space-y-3">
        <div>
          <label className="block text-xs uppercase text-gray-300 mb-1">Email</label>
          <input
            type="email"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            className="w-full p-2 rounded bg-white/20 text-white placeholder-gray-300"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="block text-xs uppercase text-gray-300 mb-1">Password</label>
          <input
            type="password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            className="w-full p-2 rounded bg-white/20 text-white placeholder-gray-300"
            autoComplete="current-password"
          />
        </div>
      </div>
      {authError && (
        <div className="bg-red-500/30 text-red-100 text-sm p-2 rounded">{authError}</div>
      )}
      <div className="flex items-center justify-between text-sm text-gray-300">
        <button className="underline hover:text-white" onClick={() => { setAuthMode('register'); setAuthError(''); }} type="button">
          Need to create an account?
        </button>
        <button className="underline hover:text-white" onClick={() => { setAuthMode('choose'); setAuthError(''); }} type="button">
          Back
        </button>
      </div>
      <button
        onClick={handleLoginAndAccept}
        disabled={authLoading}
        className={`w-full py-2 rounded font-bold transition ${authLoading ? 'bg-gray-500 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
      >
        {authLoading ? 'Signing in...' : 'Sign in & Join Team'}
      </button>
    </div>
  );

  const renderRegisterForm = () => (
    <div className="bg-white/10 p-4 rounded space-y-3">
      <h3 className="text-lg font-semibold">Create your account</h3>
      <p className="text-sm text-gray-200">
        We&rsquo;ll create your Táillí login for <span className="font-semibold">{inviteDetails.email}</span> and link you to this team.
      </p>
      <div className="space-y-3">
        <div>
          <label className="block text-xs uppercase text-gray-300 mb-1">Email</label>
          <input
            type="email"
            value={registerEmail}
            onChange={(e) => setRegisterEmail(e.target.value)}
            className="w-full p-2 rounded bg-white/20 text-white placeholder-gray-300"
            disabled={!!inviteDetails?.email}
            autoComplete="email"
          />
        </div>
        <div>
          <label className="block text-xs uppercase text-gray-300 mb-1">Password</label>
          <input
            type="password"
            value={registerPassword}
            onChange={(e) => setRegisterPassword(e.target.value)}
            className="w-full p-2 rounded bg-white/20 text-white placeholder-gray-300"
            autoComplete="new-password"
          />
        </div>
        <div>
          <label className="block text-xs uppercase text-gray-300 mb-1">Confirm Password</label>
          <input
            type="password"
            value={registerConfirmPassword}
            onChange={(e) => setRegisterConfirmPassword(e.target.value)}
            className="w-full p-2 rounded bg-white/20 text-white placeholder-gray-300"
            autoComplete="new-password"
          />
        </div>
      </div>
      {authError && (
        <div className="bg-red-500/30 text-red-100 text-sm p-2 rounded">{authError}</div>
      )}
      <div className="flex items-center justify-between text-sm text-gray-300">
        <button className="underline hover:text-white" onClick={() => { setAuthMode('login'); setAuthError(''); }} type="button">
          Already have an account?
        </button>
        <button className="underline hover:text-white" onClick={() => { setAuthMode('choose'); setAuthError(''); }} type="button">
          Back
        </button>
      </div>
      <button
        onClick={handleRegisterAndAccept}
        disabled={authLoading}
        className={`w-full py-2 rounded font-bold transition ${authLoading ? 'bg-gray-500 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'}`}
      >
        {authLoading ? 'Creating account...' : 'Create account & Join Team'}
      </button>
    </div>
  );

  if (isLoading) {
    return <div className="text-center mt-10">Loading invitation details...</div>;
  }

  return (
    <div className="container mx-auto mt-10 p-4 max-w-md bg-gray-800 rounded-lg shadow-lg text-white">
      <h1 className="text-2xl font-bold mb-6 text-center">Team Invitation</h1>
      
      {error && <div className="bg-red-500/30 text-red-200 p-3 rounded mb-4">{error}</div>}

      {inviteDetails ? (
        <div className="space-y-5">
          <div className="space-y-2">
            <p>You have been invited to join:</p>
            <p className="text-xl font-semibold text-center bg-white/10 p-3 rounded">{inviteDetails.teamName}</p>
            <p>Role: <span className="font-medium">{inviteDetails.role}</span></p>
            <p>Invite sent to: <span className="font-medium">{inviteDetails.email}</span></p>
          </div>

          {!user ? (
            authMode === 'login'
              ? renderLoginForm()
              : authMode === 'register'
                ? renderRegisterForm()
                : renderAuthOptions()
          ) : (() => {
            const inviteEmail = inviteDetails.email?.toLowerCase();
            const currentEmail = user?.user?.email?.toLowerCase();

            if (inviteEmail && currentEmail === inviteEmail) {
              return (
                <div className="space-y-3">
                  <div className="bg-green-500/20 text-green-100 p-3 rounded text-sm">
                    You&rsquo;re signed in as the invited email. Click below to finish joining the team.
                  </div>
                  <button 
                    onClick={handleAcceptInvite}
                    disabled={accepting}
                    className={`w-full py-2 px-4 rounded font-bold transition duration-200 ${accepting ? 'bg-gray-500 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                  >
                    {accepting ? 'Accepting...' : 'Accept Invitation'}
                  </button>
                </div>
              );
            }

            return (
              <div className="bg-yellow-500/30 text-yellow-200 p-3 rounded space-y-3">
                <p>
                  You&rsquo;re signed in as <span className="font-semibold">{user?.user?.email || 'another account'}</span>.
                  This invite is for <span className="font-semibold">{inviteDetails.email}</span>.
                </p>
                <button
                  onClick={async () => {
                    await authService.logout();
                    await fetchUserData();
                    setAuthMode('login');
                  }}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-2 px-4 rounded"
                >
                  Log out and switch accounts
                </button>
              </div>
            );
          })()}
        </div>
      ) : (
         !error && <p className="text-center">Could not load invitation details.</p>
      )}
    </div>
  );
}

export default AcceptInvite;
